import React, { useContext } from 'react';
import { observer } from 'mobx-react';
import classNames from 'classnames';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Text } from '@wix/design-system';
import { AppContextProvider } from '../AppLoader';
import { ROUTES } from '../../routes';
import { dataHooks } from '../../dataHooks';
import sl from '../SocialLoginButton/SocialLoginButton.scss';

/**
 * "Scan QR" icon, exported 1:1 from the Symphony MKT Figma (node 6404:5930):
 * four rounded corner brackets framing a 2×2 grid of dots. Inlined so it inherits
 * `currentColor` (the button's black label color) and needs no bundled asset.
 */
const QrGlyph: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
    focusable="false"
  >
    <path
      fill="currentColor"
      d="M5.75 14c.41 0 .75.34.75.75V17c0 .27.23.5.5.5h2.25c.41 0 .75.34.75.75s-.34.75-.75.75H7c-1.1 0-2-.9-2-2v-2.25c0-.41.34-.75.75-.75Z"
    />
    <path
      fill="currentColor"
      d="M18.25 14c.41 0 .75.34.75.75V17c0 1.1-.9 2-2 2h-2.25c-.41 0-.75-.34-.75-.75s.34-.75.75-.75H17c.27 0 .5-.23.5-.5v-2.25c0-.41.34-.75.75-.75Z"
    />
    <path
      fill="currentColor"
      d="M10.5 13c.28 0 .5.22.5.5v2c0 .28-.22.5-.5.5h-2a.5.5 0 0 1-.5-.5v-2c0-.28.22-.5.5-.5h2ZM15.5 13c.28 0 .5.22.5.5v2c0 .28-.22.5-.5.5h-2a.5.5 0 0 1-.5-.5v-2c0-.28.22-.5.5-.5h2ZM10.5 8c.28 0 .5.22.5.5v2c0 .28-.22.5-.5.5h-2a.5.5 0 0 1-.5-.5v-2c0-.28.22-.5.5-.5h2ZM15.5 8c.28 0 .5.22.5.5v2c0 .28-.22.5-.5.5h-2a.5.5 0 0 1-.5-.5v-2c0-.28.22-.5.5-.5h2Z"
    />
    <path
      fill="currentColor"
      d="M9.25 5c.41 0 .75.34.75.75s-.34.75-.75.75H7c-.27 0-.5.23-.5.5v2.25c0 .41-.34.75-.75.75S5 9.66 5 9.25V7c0-1.1.9-2 2-2h2.25ZM17 5c1.1 0 2 .9 2 2v2.25c0 .41-.34.75-.75.75s-.75-.34-.75-.75V7c0-.27-.23-.5-.5-.5h-2.25c-.41 0-.75-.34-.75-.75S14.34 5 14.75 5H17Z"
    />
  </svg>
);

/**
 * Desktop-only "Scan QR with the app" button for the Symphony brand, shown next
 * to the social-login buttons. It navigates to the existing device-authorization
 * route (`/device/authorize`) where the user scans a QR with the Symphony mobile
 * app to sign in.
 *
 * Rendered only when `isSymphony && !isMobile`:
 * - QR sign-in is a desktop-to-mobile handoff, so it makes no sense on mobile.
 * - It reuses the Symphony social-button look (`newDesign` neutral outlined
 *   button, driven by the same `--social-button-*` CSS variables) so it lines up
 *   pixel-for-pixel with the Google/Facebook/Apple buttons above it.
 *
 * `location.search` is preserved on navigation so the resolved preset
 * (userType=symphony / preset query param) carries over to the device route and
 * that page renders its Symphony-branded variant.
 */
export const SymphonyQrScanButton: React.FC = observer(() => {
  const {
    rootStore: { displayStore },
  } = useContext(AppContextProvider);
  const { t } = useTranslation();
  const location = useLocation();

  if (!displayStore.isSymphony || displayStore.isMobile) {
    return null;
  }

  // Mirror CustomLoginButton's inline style so the button matches the social
  // buttons: same width/height/radius plus the preset's neutral-outlined color
  // scheme via the shared --social-button-* CSS variables.
  const buttonStyle: React.CSSProperties = {
    width: displayStore.socialButtonElementWidth,
    height: displayStore.socialButtonHeight,
    // The provider buttons are <button>s (border-box); this renders as an <a>,
    // which defaults to content-box — without this the 12px padding + 1px border
    // add ~26px width / 2px height and the button looks larger than its row.
    boxSizing: 'border-box',
    borderRadius: displayStore.socialButtonBorderRadius,
    borderStyle: displayStore.socialButtonBorderColor ? 'solid' : undefined,
    // It renders as an <a> (react-router Link); pin the text color so the QR
    // glyph (currentColor) matches the button label and kill the default anchor
    // underline — otherwise it inherits the theme link blue + underline.
    color: displayStore.socialButtonTextColor ?? '#000000',
    textDecoration: 'none',
    ...({
      '--social-button-border-color': displayStore.socialButtonBorderColor,
      '--social-button-border-width': displayStore.socialButtonBorderWidth,
      '--social-button-hover-bg': displayStore.socialButtonHoverBackgroundColor,
      '--social-button-hover-border': displayStore.socialButtonHoverBorderColor,
      '--social-button-active-bg':
        displayStore.socialButtonActiveBackgroundColor,
      '--social-button-active-border':
        displayStore.socialButtonActiveBorderColor,
      '--social-button-text-color': displayStore.socialButtonTextColor,
      '--social-button-focus-outline': displayStore.socialButtonFocusOutline,
      '--social-button-focus-outline-offset':
        displayStore.socialButtonFocusOutlineOffset,
      '--wds-font-size-body-medium': displayStore.socialButtonFontSize,
    } as React.CSSProperties),
  };

  return (
    <Link
      to={ROUTES.DEVICE_AUTHORIZE + location.search}
      style={buttonStyle}
      className={classNames(
        sl.socialButton,
        sl.newDesign,
        sl.centeredLayout,
        sl.a11yFocus,
      )}
      data-hook={dataHooks.socials.qrScanButton}
    >
      <QrGlyph className={sl.logo} />
      {/* Render the label with WDS <Text> (same as the provider buttons) so it
          picks up the Wix Madefor font-family and the shared `.title` styling —
          a plain <span> would fall back to the default serif. */}
      <Text light className={sl.title} size="medium">
        {t('social.symphony.qr.button', {
          defaultValue: 'Scan QR with the app',
        })}
      </Text>
    </Link>
  );
});
